function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { INPUT } from "../../components/form-styles";

/*
 * Rich text for the composer's Text / Offer / Image+text / Columns copy
 * (2026-10-04). A contenteditable with a six-button toolbar that produces
 * clean HTML limited to p / strong / em / a / ul / li / h2 / br; pasted
 * markup is sanitized to the same set. "Edit HTML" swaps in the plain
 * textarea. Both views read and write the same `html` string.
 */

const ALLOWED = {
  P: "p",
  STRONG: "strong",
  B: "strong",
  EM: "em",
  I: "em",
  A: "a",
  UL: "ul",
  LI: "li",
  H2: "h2",
  H1: "h2",
  H3: "h2",
  BR: "br"
};
const UNWRAP = {
  DIV: 1,
  SPAN: 1,
  FONT: 1,
  SECTION: 1,
  ARTICLE: 1,
  TABLE: 1,
  TBODY: 1,
  TR: 1,
  TD: 1,
  TH: 1,
  OL: 1,
  BLOCKQUOTE: 1,
  PRE: 1,
  CODE: 1,
  U: 1,
  S: 1,
  SMALL: 1,
  LABEL: 1,
  HEADER: 1,
  FOOTER: 1,
  MAIN: 1,
  NAV: 1,
  H4: 1,
  H5: 1,
  H6: 1
};
const DROP = {
  SCRIPT: 1,
  STYLE: 1,
  IFRAME: 1,
  OBJECT: 1,
  EMBED: 1,
  IMG: 1,
  SVG: 1,
  VIDEO: 1,
  AUDIO: 1,
  FORM: 1,
  INPUT: 1,
  BUTTON: 1,
  META: 1,
  LINK: 1,
  HEAD: 1,
  TITLE: 1,
  NOSCRIPT: 1
};
function safeHref(h) {
  const v = String(h || "").trim();
  if (!v) return "";
  if (/^(https?:|mailto:|tel:|\{\{)/i.test(v)) return v;
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return ""; // javascript:, data: etc.
  return "https://" + v.replace(/^\/+/, "");
}

/** Sanitize any HTML string (or a DOM fragment) to the allowed set. Merge fields survive as text. */
export function sanitizeHtml(html) {
  const root = document.createElement("div");
  root.innerHTML = String(html || "");
  const walk = node => {
    const out = [];
    Array.from(node.childNodes).forEach(n => {
      if (n.nodeType === 3) {
        if (n.nodeValue) out.push(n.nodeValue.replace(/ /g, " "));
        return;
      }
      if (n.nodeType !== 1) return;
      const tag = n.tagName;
      if (DROP[tag]) return;
      const inner = walk(n);
      if (ALLOWED[tag]) {
        const t = ALLOWED[tag];
        if (t === "br") {
          out.push("<br>");
          return;
        }
        if (t === "a") {
          const href = safeHref(n.getAttribute("href"));
          if (!href) {
            out.push(inner);
            return;
          }
          out.push(`<a href="${href.replace(/"/g, "&quot;")}">${inner}</a>`);
          return;
        }
        if (t === "li" && !inner.trim()) return;
        if (t === "ul" && !inner.trim()) return;
        if (t === "p" && !inner.trim() && !/<br>/.test(inner)) {
          out.push("<p><br></p>");
          return;
        }
        out.push(`<${t}>${inner}</${t}>`);
        return;
      }
      // Unknown or unwrapped tag: keep its children; block-ish tags become paragraphs
      if (/^(DIV|TD|TH|BLOCKQUOTE|PRE|H4|H5|H6|SECTION|ARTICLE|HEADER|FOOTER|MAIN)$/.test(tag) && inner.trim()) {
        out.push(inner.trim().startsWith("<") ? inner : `<p>${inner}</p>`);
        return;
      }
      out.push(inner);
    });
    return out.join("");
  };
  let s = walk(root);
  // collapse empty leading/trailing paragraphs and whitespace noise
  s = s.replace(/(<p><br><\/p>\s*){2,}/g, "<p><br></p>").replace(/^\s*(<p><br><\/p>)+/, "").replace(/(<p><br><\/p>)+\s*$/, "").trim();
  // bare text (no block) gets a paragraph
  if (s && !/^<(p|ul|h2)/.test(s)) s = `<p>${s}</p>`;
  return s;
}
export function textOf(html) {
  return String(html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}
export default class RichText extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "emit", () => {
      const el = this.ref.current;
      if (!el) return;
      const clean = sanitizeHtml(el.innerHTML);
      this._lastSet = clean;
      if (clean !== (this.props.value || "")) this.props.onChange(clean);
    });
    _defineProperty(this, "onSel", () => {
      const el = this.ref.current;
      if (!el || this.state.mode !== "rich") return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) {
        if (Object.keys(this.state.fmt).length) this.setState({
          fmt: {}
        });
        return;
      }
      const fmt = {};
      try {
        fmt.bold = document.queryCommandState("bold");
        fmt.italic = document.queryCommandState("italic");
        fmt.ul = document.queryCommandState("insertUnorderedList");
        let n = sel.anchorNode;
        while (n && n !== el) {
          if (n.tagName === "H2") fmt.h2 = true;
          if (n.tagName === "A") fmt.a = n;
          n = n.parentNode;
        }
      } catch (e) {/* noop */}
      const changed = ["bold", "italic", "ul", "h2"].some(k => !!fmt[k] !== !!this.state.fmt[k]) || fmt.a !== this.state.fmt.a;
      if (changed) this.setState({
        fmt
      });
    });
    _defineProperty(this, "cmd", (name, arg) => {
      const el = this.ref.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand(name, false, arg);
      } catch (e) {/* noop */}
      this.emit();
      this.onSel();
    });
    _defineProperty(this, "heading", () => {
      const on = this.state.fmt.h2;
      this.cmd("formatBlock", on ? "p" : "h2");
    });
    _defineProperty(this, "clear", () => {
      this.cmd("removeFormat");
      this.cmd("unlink");
      if (this.state.fmt.h2) this.cmd("formatBlock", "p");
    });
    _defineProperty(this, "openLink", () => {
      const el = this.ref.current;
      if (!el) return;
      const sel = window.getSelection();
      let text = sel && sel.rangeCount && el.contains(sel.anchorNode) ? sel.toString() : "";
      let url = "";
      const a = this.state.fmt.a;
      if (a) {
        url = a.getAttribute("href") || "";
        if (!text) text = a.textContent;
      }
      try {
        this._range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
      } catch (e) {
        this._range = null;
      }
      this.setState({
        linkOpen: true,
        linkUrl: url,
        linkText: text
      });
    });
    _defineProperty(this, "applyLink", () => {
      const el = this.ref.current;
      if (!el) return;
      const href = safeHref(this.state.linkUrl);
      el.focus();
      const sel = window.getSelection();
      if (this._range) {
        try {
          sel.removeAllRanges();
          sel.addRange(this._range);
        } catch (e) {/* noop */}
      }
      if (!href) {
        this.cmd("unlink");
        this.setState({
          linkOpen: false
        });
        return;
      }
      const a = this.state.fmt.a;
      if (a && el.contains(a)) {
        a.setAttribute("href", href);
        if (this.state.linkText.trim()) a.textContent = this.state.linkText.trim();
        this.emit();
      } else if (sel && sel.rangeCount && !sel.isCollapsed) {
        this.cmd("createLink", href);
      } else {
        const label = this.state.linkText.trim() || href;
        this.cmd("insertHTML", `<a href="${href.replace(/"/g, "&quot;")}">${label.replace(/</g, "&lt;")}</a>`);
      }
      this.setState({
        linkOpen: false,
        linkUrl: "",
        linkText: ""
      });
    });
    _defineProperty(this, "onPaste", e => {
      const html = e.clipboardData && e.clipboardData.getData("text/html");
      const text = e.clipboardData && e.clipboardData.getData("text/plain");
      if (!html && !text) return;
      e.preventDefault();
      const clean = html ? sanitizeHtml(html) : String(text).split(/\n{2,}/).map(p => `<p>${p.replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>`).join("");
      this.cmd("insertHTML", clean);
    });
    _defineProperty(this, "onKey", e => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey) {
        const k = e.key.toLowerCase();
        if (k === "b") {
          e.preventDefault();
          this.cmd("bold");
        } else if (k === "i") {
          e.preventDefault();
          this.cmd("italic");
        } else if (k === "k") {
          e.preventDefault();
          this.openLink();
        }
      }
    });
    this.state = {
      mode: "rich",
      linkOpen: false,
      linkUrl: "",
      linkText: "",
      fmt: {}
    };
    this.ref = /*#__PURE__*/React.createRef();
    this._lastSet = null;
  }
  componentDidMount() {
    this.sync();
    document.addEventListener("selectionchange", this.onSel);
  }
  componentDidUpdate(prevProps, prevState) {
    if (this.state.mode === "rich" && (prevProps.value !== this.props.value || prevState.mode !== this.state.mode)) this.sync();
  }
  componentWillUnmount() {
    document.removeEventListener("selectionchange", this.onSel);
  }
  sync() {
    const el = this.ref.current;
    if (!el) return;
    const v = this.props.value || "";
    if (v === this._lastSet) return; // our own emit
    const clean = sanitizeHtml(v);
    if (el.innerHTML !== clean) el.innerHTML = clean || "<p><br></p>";
    this._lastSet = v;
  }
  /** Insert text (a merge field) at the caret, or append. */
  insertText(token) {
    if (this.state.mode !== "rich") {
      this.props.onChange((this.props.value || "").replace(/<\/p>\s*$/, ` ${token}</p>`) || (this.props.value || "") + token);
      return;
    }
    const el = this.ref.current;
    if (!el) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) this.cmd("insertText", token);else {
      el.focus();
      try {
        sel.selectAllChildren(el);
        sel.collapseToEnd();
      } catch (e) {/* noop */}
      this.cmd("insertText", token);
    }
  }
  render() {
    const {
      value,
      rows,
      placeholder
    } = this.props;
    const {
      mode,
      fmt,
      linkOpen
    } = this.state;
    const B = ({
      on,
      title,
      label,
      onClick,
      cls
    }) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cmp-tb" + (on ? " on" : "") + (cls ? " " + cls : ""),
      title: title,
      onMouseDown: e => e.preventDefault(),
      onClick: onClick
    }, label);
    return /*#__PURE__*/React.createElement("div", {
      className: "cmp-rt"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cmp-rt-bar"
    }, mode === "rich" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(B, {
      on: fmt.bold,
      title: "Bold (\u2318B)",
      label: /*#__PURE__*/React.createElement("b", null, "B"),
      onClick: () => this.cmd("bold")
    }), /*#__PURE__*/React.createElement(B, {
      on: fmt.italic,
      title: "Italic (\u2318I)",
      label: /*#__PURE__*/React.createElement("i", null, "I"),
      onClick: () => this.cmd("italic")
    }), /*#__PURE__*/React.createElement(B, {
      on: !!fmt.a,
      title: "Link (\u2318K)",
      label: "Link",
      onClick: this.openLink
    }), /*#__PURE__*/React.createElement(B, {
      on: fmt.ul,
      title: "Bulleted list",
      label: "\u2022 List",
      onClick: () => this.cmd("insertUnorderedList")
    }), /*#__PURE__*/React.createElement(B, {
      on: fmt.h2,
      title: "Heading",
      label: "H",
      onClick: this.heading,
      cls: "h"
    }), /*#__PURE__*/React.createElement(B, {
      title: "Clear formatting",
      label: "Clear",
      onClick: this.clear
    })) : /*#__PURE__*/React.createElement("span", {
      className: "cmp-rt-note"
    }, "Allowed: <p> <strong> <em> <a> <ul> <li> <h2> <br>"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "cmp-tb right",
      onMouseDown: e => e.preventDefault(),
      onClick: () => {
        if (mode === "html") this.setState({
          mode: "rich"
        }, () => {
          this._lastSet = null;
          this.sync();
        });else this.setState({
          mode: "html"
        });
      }
    }, mode === "rich" ? "Edit HTML" : "Visual editor")), mode === "rich" ? /*#__PURE__*/React.createElement("div", {
      ref: this.ref,
      className: "cmp-rt-area",
      contentEditable: true,
      suppressContentEditableWarning: true,
      "data-placeholder": placeholder || "Write here…",
      style: {
        minHeight: rows ? rows * 22 + 16 : 96
      },
      onInput: this.emit,
      onBlur: this.emit,
      onPaste: this.onPaste,
      onKeyDown: this.onKey,
      onKeyUp: this.onSel,
      onMouseUp: this.onSel
    }) : /*#__PURE__*/React.createElement("textarea", {
      style: {
        ...INPUT,
        minHeight: rows ? rows * 22 + 16 : 96,
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 12,
        resize: "vertical"
      },
      value: value || "",
      onChange: e => this.props.onChange(e.target.value),
      onBlur: () => this.props.onChange(sanitizeHtml(value || ""))
    }), linkOpen ? /*#__PURE__*/React.createElement("div", {
      className: "cmp-rt-link"
    }, /*#__PURE__*/React.createElement("input", {
      style: {
        ...INPUT,
        flex: "1 1 180px"
      },
      autoFocus: true,
      placeholder: "https://\u2026 or {{brandWebsite}}",
      value: this.state.linkUrl,
      onChange: e => this.setState({
        linkUrl: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.applyLink();
        if (e.key === "Escape") this.setState({
          linkOpen: false
        });
      }
    }), /*#__PURE__*/React.createElement("input", {
      style: {
        ...INPUT,
        flex: "1 1 120px"
      },
      placeholder: "Link text (optional)",
      value: this.state.linkText,
      onChange: e => this.setState({
        linkText: e.target.value
      }),
      onKeyDown: e => {
        if (e.key === "Enter") this.applyLink();
      }
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle",
      style: {
        marginTop: 0
      },
      onClick: this.applyLink
    }, this.state.linkUrl.trim() ? "Apply" : "Remove link"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "territory-toggle",
      style: {
        marginTop: 0
      },
      onClick: () => this.setState({
        linkOpen: false
      })
    }, "Cancel")) : null);
  }
}